import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { reviews } from "@fm/api";
import type { ApiClient, Review } from "@fm/api";
import { getApiBaseUrl } from "../lib/config";

interface UseVehicleReviewsParams {
  vehicleId: string;
  fleetId: string;
  apiKey: string;
  apiBaseUrl?: string;
  enabled?: boolean;
}

const calculateAverageRating = (reviews: Review[]): number => {
  if (reviews.length === 0) return 0;
  const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
  return sum / reviews.length;
};

/**
 * Hook for fetching vehicle review statistics (average rating and total count)
 * This provides accurate stats even when reviews are paginated
 */
export function useVehicleReviewsStats({
  vehicleId,
  fleetId,
  apiKey,
  apiBaseUrl,
  enabled = true,
}: UseVehicleReviewsParams) {
  const apiClient: ApiClient = {
    baseUrl: getApiBaseUrl(apiBaseUrl),
    apiKey,
  };

  return useQuery({
    queryKey: ["vehicle-reviews-stats", vehicleId, fleetId],
    queryFn: async () => {
      const response = await reviews.getStats(apiClient, {
        vehicle_id: vehicleId,
      });
      return response.data;
    },
    enabled: enabled && !!vehicleId && !!fleetId && !!apiKey,
  });
}

/**
 * Hook for fetching paginated vehicle reviews
 * Supports infinite scroll / "Load More" functionality
 */
export function useVehicleReviews({
  vehicleId,
  fleetId,
  apiKey,
  apiBaseUrl,
  enabled = true,
}: UseVehicleReviewsParams) {
  const apiClient: ApiClient = {
    baseUrl: getApiBaseUrl(apiBaseUrl),
    apiKey,
  };

  return useInfiniteQuery({
    queryKey: ["vehicle-reviews", vehicleId, fleetId],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await reviews.list(apiClient, {
        vehicle_id: vehicleId,
        page: pageParam,
        page_size: 5, // Load 5 reviews at a time
      });

      return response;
    },
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = allPages.length;

      const hasMore = lastPage.data.length === 5;
      return hasMore ? currentPage + 1 : undefined;
    },
    enabled: enabled && !!vehicleId && !!fleetId && !!apiKey,
    select: (data) => {
      const allReviews = data.pages.flatMap((page) =>
        page.data.filter((review: Review) => review.is_visible),
      );

      return {
        reviews: allReviews,
        hasNextPage:
          data.pages.length > 0 &&
          data.pages[data.pages.length - 1]?.data.length === 5,
      };
    },
    initialPageParam: 1,
  });
}

/**
 * Hook to mark a review as helpful (public endpoint, no auth needed).
 * Optimistically bumps helpful_count across paginated pages.
 */
export function useMarkReviewHelpful({
  vehicleId,
  fleetId,
  apiKey,
  apiBaseUrl,
}: Omit<UseVehicleReviewsParams, "enabled">) {
  const apiClient: ApiClient = {
    baseUrl: getApiBaseUrl(apiBaseUrl),
    apiKey,
  };
  const queryClient = useQueryClient();
  const queryKey = ["vehicle-reviews", vehicleId, fleetId];

  return useMutation({
    mutationFn: (reviewId: string) => reviews.markHelpful(apiClient, reviewId),
    onMutate: async (reviewId: string) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old: unknown) => {
        if (!old || typeof old !== "object" || !("pages" in old)) return old;
        const data = old as {
          pages: { data: Review[] }[];
          pageParams: unknown[];
        };
        return {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            data: page.data.map((r) =>
              r.id === reviewId
                ? { ...r, helpful_count: r.helpful_count + 1 }
                : r,
            ),
          })),
        };
      });

      return { previous };
    },
    onError: (_err, _reviewId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
  });
}
